import { useTheme } from "./hooks";
import type { ResolvedTheme } from "./storage";

/** Structural ("chrome") colours a chart needs beyond its data series. */
export interface ChartTheme {
  /** Faint background gridlines. */
  grid: string;
  /** Axis lines + tick labels. */
  axis: string;
  /** Hover cursor fill (bar charts). */
  cursor: string;
  /** "No data" cell fill (heatmap). */
  absent: string;
  /** The plot surface (= card); e.g. marker outlines that "cut out" the bg. */
  surface: string;
}

/**
 * Chart chrome per theme. These mirror design tokens (tokens.css) —
 * grid=`--border`, axis=`--muted-foreground`, cursor/absent=`--muted` — but are
 * kept as concrete strings here because Recharts and plain SVG take colours as
 * attributes, where `hsl(var(--token))` does not resolve. Keep in sync with
 * tokens.css if those tokens change.
 */
const CHART_THEME: Record<ResolvedTheme, ChartTheme> = {
  light: {
    grid: "hsl(214 20% 90%)",
    axis: "hsl(215 16% 47%)",
    cursor: "hsl(210 24% 96%)",
    absent: "hsl(210 24% 96%)",
    surface: "hsl(0 0% 100%)",
  },
  dark: {
    grid: "hsl(216 16% 25%)",
    axis: "hsl(216 14% 66%)",
    cursor: "hsl(216 18% 20%)",
    absent: "hsl(216 18% 20%)",
    surface: "hsl(216 24% 13%)",
  },
};

/**
 * The active theme's chart chrome colours. Derived purely from `resolvedTheme`
 * (context) so charts re-render with the right colours the instant the theme
 * flips — no DOM reads, no flash.
 *
 * The categorical *series* palette (CHART_COLORS / CRITERIA_COLORS) is
 * intentionally NOT themed: it's one fixed, mid-toned ramp chosen to stay
 * legible on either ground, so a given series keeps its colour across a toggle.
 */
export function useChartTheme(): ChartTheme {
  const { resolvedTheme } = useTheme();
  return CHART_THEME[resolvedTheme];
}
